/**
 * SortableList — genericky presuvatelny seznam s drag & drop.
 *
 * Pouziti:
 *   <SortableList
 *     items={items}
 *     keyFn={(item) => item.id}
 *     onReorder={(newItems) => save(newItems)}
 *     renderItem={(item, { dragHandleProps, isActive }) => (
 *       <MyCard item={item} dragHandleProps={dragHandleProps} />
 *     )}
 *   />
 *
 * DragHandle:
 *   <button {...dragHandleProps}><GripVertical size={15} /></button>
 */
import { type ReactNode } from 'react';
import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

export interface DragHandleProps {
  [key: string]: unknown;
}

interface RenderContext {
  dragHandleProps: DragHandleProps;
  isDragging: boolean;
}

interface SortableListProps<T> {
  items: T[];
  keyFn: (item: T) => string;
  onReorder: (reordered: T[]) => void;
  renderItem: (item: T, ctx: RenderContext) => ReactNode;
  gap?: string; // tailwind gap class, default 'space-y-2'
}

function SortableItem<T>({ id, item, renderItem }: {
  id: string;
  item: T;
  renderItem: (item: T, ctx: RenderContext) => ReactNode;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });
  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        position: isDragging ? 'relative' : undefined,
        zIndex: isDragging ? 50 : undefined,
      }}
    >
      {renderItem(item, { dragHandleProps: { ...attributes, ...listeners }, isDragging })}
    </div>
  );
}

export function SortableList<T>(
  { items, keyFn, onReorder, renderItem, gap = 'space-y-2' }: SortableListProps<T>,
) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = items.findIndex((item) => keyFn(item) === active.id);
    const newIdx = items.findIndex((item) => keyFn(item) === over.id);
    if (oldIdx === -1 || newIdx === -1) return;
    onReorder(arrayMove(items, oldIdx, newIdx));
  }

  const ids = items.map(keyFn);

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        <div className={gap}>
          {items.map((item) => (
            <SortableItem key={keyFn(item)} id={keyFn(item)} item={item} renderItem={renderItem} />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}
